import { userSettingsRepository } from '@/api/userSettingsRepository';

// The dirty guard (settings sync contract §4). Every local change of a synced
// setting is recorded here BEFORE its single-column patch is sent and removed
// once the patch succeeded with no newer edit of the same column meanwhile.
// A re-sync (sign-in, USER_UPDATED, tab back after a while) keeps every
// column listed here instead of applying the server's value, and sends it
// again — so a change made seconds earlier is never undone, and a change made
// offline survives the next sign-in.
//
// Shared by settingsStore, localeStore, themeStore and teachingStore; it
// imports no store, so none of them form an import cycle through it.

const pending = new Map<string, number>();
/** Stamp of the latest local edit per column (survives a successful patch). */
const lastEdit = new Map<string, number>();
let seq = 0;

/** A point in the edit sequence — take it before a sync's fetch starts. */
export function editMark(): number {
  return seq;
}

/** Columns edited locally after `mark` (even if their patch already landed:
 *  a fetch that started before the patch may still carry the old value). */
export function editedSince(mark: number): string[] {
  return [...lastEdit.entries()].filter(([, s]) => s > mark).map(([c]) => c);
}

/** Columns with a pending (in-flight or failed) local edit. */
export function pendingColumns(): string[] {
  return [...pending.keys()];
}

/** Account switch / sign-out: another account's edits must never be sent. */
export function clearPendingSettings(): void {
  pending.clear();
  lastEdit.clear();
}

/**
 * Patches exactly `fields` (column → value) on the signed-in user's row.
 * Signed out → nothing is recorded (there is no account to write to; the
 * local value is the device's until someone signs in). Never throws.
 */
export async function pushSettingFields(fields: Record<string, unknown>): Promise<boolean> {
  const cols = Object.keys(fields);
  if (cols.length === 0) return true;
  const stamp = ++seq;
  for (const c of cols) {
    pending.set(c, stamp);
    lastEdit.set(c, stamp);
  }
  let uid: string | undefined;
  try {
    uid = await userSettingsRepository.signedInUserId();
  } catch {
    uid = undefined;
  }
  if (!uid) {
    for (const c of cols) if (pending.get(c) === stamp) pending.delete(c);
    return false;
  }
  try {
    // Columns the server lacks come back as skipped: they stay on this
    // device, so there is nothing left to retry for them either.
    await userSettingsRepository.patch(fields);
    for (const c of cols) if (pending.get(c) === stamp) pending.delete(c);
    return true;
  } catch {
    return false; // stays pending → the next sync sends it again
  }
}
